import { TransactionStatus } from "Infrastructure/Api/Api";
import { useEffect, useRef } from "react";
import {
  PAYMENT_FLOW_PARAM,
  hasPaymentFlowMarker,
} from "Utils/PaymentFlowUtils";

const STATUS_PARAM_LOOKUP: Partial<Record<TransactionStatus, string>> = {
  [TransactionStatus.Success]: "success",
  [TransactionStatus.Fail]: "fail",
  [TransactionStatus.Cancelled]: "cancelled",
};

type Props = {
  transactionPublicID: string;
  transactionStatus?: TransactionStatus;
  returnUrl?: string | null;
  /**
   * Gate for viewers who must NOT be redirected — only the paying customer (debtor)
   * should return to the e-shop, not e.g. the merchant opening the same detail.
   */
  isEnabled?: boolean;
};

/**
 * E-commerce gateway flow: when the transaction carries a returnUrl and reaches a terminal
 * state (Success/Fail/Cancelled), redirect the customer's browser back to the e-shop with
 * `status` and `transactionId` query parameters appended.
 *
 * Fires only when the customer is actually finishing a payment: either the page was opened
 * from the bank return (paymentFlow URL marker) or the terminal status transition happened
 * live while watching. Opening the detail of a long-finished gateway payment from history
 * therefore never bounces the viewer out of the app. No-op for regular Mo.one payments
 * (returnUrl is null).
 */
export const useGatewayReturnRedirect = ({
  transactionPublicID,
  transactionStatus,
  returnUrl,
  isEnabled = true,
}: Props) => {
  const hasRedirected = useRef(false);
  const initialStatusRef = useRef<TransactionStatus | null>(null);

  if (transactionStatus && initialStatusRef.current === null) {
    initialStatusRef.current = transactionStatus;
  }

  const statusParam = transactionStatus
    ? STATUS_PARAM_LOOKUP[transactionStatus]
    : undefined;

  const isArrivedFromPaymentFlow = hasPaymentFlowMarker(window.location.search);
  const wasTerminalOnFirstLoad =
    initialStatusRef.current !== null &&
    STATUS_PARAM_LOOKUP[initialStatusRef.current] !== undefined;
  const isFinishingPayment =
    isArrivedFromPaymentFlow || !wasTerminalOnFirstLoad;

  useEffect(() => {
    if (
      !isEnabled ||
      !returnUrl ||
      !statusParam ||
      !isFinishingPayment ||
      hasRedirected.current
    ) {
      return;
    }

    let target: URL;
    try {
      target = new URL(returnUrl);
    } catch {
      return;
    }

    // Backend validates the scheme at initiation; re-check here so a javascript:/data: URL
    // can never be navigated to even if it somehow reached the stored transaction.
    if (target.protocol !== "https:" && target.protocol !== "http:") {
      return;
    }

    target.searchParams.set("status", statusParam);
    target.searchParams.set("transactionId", transactionPublicID);

    hasRedirected.current = true;

    // Strip the paymentFlow marker from the history entry BEFORE leaving — otherwise the
    // browser Back button from the e-shop would land on the marker URL and instantly
    // re-redirect, trapping the customer outside the app.
    const currentUrl = new URL(window.location.href);
    if (currentUrl.searchParams.has(PAYMENT_FLOW_PARAM)) {
      currentUrl.searchParams.delete(PAYMENT_FLOW_PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        currentUrl.toString(),
      );
    }

    window.location.href = target.toString();
  }, [
    isEnabled,
    returnUrl,
    statusParam,
    isFinishingPayment,
    transactionPublicID,
  ]);
};
